import { type ReactNode, useEffect } from "react";
import "./index.css";

import chartRouting from "./chart-routing.svg";
import chartSpend from "./chart-spend.svg";
import heroGraphic from "./graphic-hero.svg";
import pipelineGraphic from "./graphic-pipeline.svg";
import logo from "./logo.svg";

type RoutePath = "/" | "/platform" | "/developers" | "/pricing" | "/solutions";

const siteNav = [
  { href: "/" as const, label: "Home" },
  { href: "/platform" as const, label: "Platform" },
  { href: "/developers" as const, label: "Developers" },
  { href: "/pricing" as const, label: "Pricing" },
  { href: "/solutions" as const, label: "Use Cases" },
];

const pageMeta: Record<RoutePath, { title: string; description: string }> = {
  "/": {
    title: "CaptchaSolver.io | Captcha Infrastructure For Production Automation",
    description:
      "CaptchaSolver.io brings modern challenge coverage, clean developer ergonomics, and commercial clarity to teams running serious browser automation.",
  },
  "/platform": {
    title: "Platform | CaptchaSolver.io",
    description:
      "See how CaptchaSolver.io handles task intake, routing, delivery, and control surfaces for modern captcha solving workloads.",
  },
  "/developers": {
    title: "Developers | CaptchaSolver.io",
    description:
      "Explore the developer surface behind CaptchaSolver.io, from readable task models to callback support and browser automation fit.",
  },
  "/pricing": {
    title: "Pricing | CaptchaSolver.io",
    description:
      "Usage-based pricing, free sandbox access, scale discounts, and enterprise lanes for teams buying captcha solving infrastructure.",
  },
  "/solutions": {
    title: "Use Cases | CaptchaSolver.io",
    description:
      "See where CaptchaSolver.io fits: signup flows, account operations, monitoring, QA, and browser automation programs that need reliable solves.",
  },
};

const challengeFamilies = [
  "reCAPTCHA v2",
  "reCAPTCHA v3",
  "reCAPTCHA Enterprise",
  "Cloudflare Turnstile",
  "hCaptcha",
  "Arkose Labs",
  "GeeTest",
  "DataDome",
  "AWS WAF",
  "Image To Text",
  "Audio Captcha",
  "Browser Extension",
];

const deliveryModes = [
  "ProxyLess tasks for fast starts",
  "Bring-your-own-proxy lanes for tighter control",
  "Polling and callback delivery",
  "Request shapes built around production workflows",
  "Browser automation and extension-friendly integration",
];

const developerStack = [
  "Python SDK",
  "Node SDK",
  "Go SDK",
  "PHP SDK",
  "Selenium",
  "Playwright",
  "Puppeteer",
  "Cypress",
  "Appium",
  "WebdriverIO",
  "Scrapy",
  "Custom internal tooling",
];

const capabilityCards = [
  {
    label: "Coverage",
    title: "The challenge families teams actually compare.",
    copy: "reCAPTCHA variants, Turnstile, hCaptcha, Arkose, GeeTest, DataDome, AWS WAF, image, and audio.",
  },
  {
    label: "Delivery",
    title: "Flexible enough for different traffic shapes.",
    copy: "ProxyLess, own-proxy, polling, callbacks, and extension-friendly workflows in one surface.",
  },
  {
    label: "Developer Fit",
    title: "Built around the stacks teams already use.",
    copy: "Python, Node, Go, PHP, Selenium, Playwright, Puppeteer, Cypress, and internal automation tooling.",
  },
  {
    label: "Commercial",
    title: "Simple to start, sensible to scale.",
    copy: "Free sandbox access, usage pricing, volume discounts, and enterprise packaging when procurement shows up.",
  },
];

const productPillars = [
  {
    label: "Cleaner task model",
    title: "Readable inputs and result paths.",
    copy: "The API surface is meant to feel deliberate rather than inherited from a decade of category drift.",
  },
  {
    label: "Operational clarity",
    title: "A calmer control plane for a noisy category.",
    copy: "Coverage, routing, and commercial packaging stay legible as traffic grows and requirements sharpen.",
  },
  {
    label: "Migration ready",
    title: "Built for teams leaving older solver vendors.",
    copy: "The site and product framing make switching feel practical, not risky or time-consuming.",
  },
];

const buyerQuestions = [
  {
    question: "What challenge types matter most in practice?",
    answer:
      "The baseline is reCAPTCHA variants, Turnstile, hCaptcha, Arkose, GeeTest, DataDome, AWS WAF, image, and audio.",
  },
  {
    question: "Does proxy strategy stay flexible?",
    answer:
      "Yes. Teams usually want a fast ProxyLess path plus bring-your-own-proxy lanes when control starts to matter.",
  },
  {
    question: "Will it fit browser automation workflows?",
    answer:
      "That is core to the pitch. The site should clearly support Selenium, Playwright, Puppeteer, Cypress, and similar stacks.",
  },
  {
    question: "How should pricing evolve with usage?",
    answer:
      "A serious offer starts with free exploration, moves into usage-based access, and stays credible at volume and enterprise scale.",
  },
];

const platformLifecycle = [
  {
    number: "01",
    title: "Intake",
    copy: "Capture the task family, site data, proxy choice, and delivery mode without bloating the request.",
  },
  {
    number: "02",
    title: "Routing",
    copy: "Match the solve to challenge type, traffic pattern, and proxy lane through one consistent surface.",
  },
  {
    number: "03",
    title: "Delivery",
    copy: "Return results through polling or callbacks depending on how the surrounding workflow is orchestrated.",
  },
  {
    number: "04",
    title: "Control",
    copy: "Keep operators close to throughput, spend shape, and packaging decisions as the program scales.",
  },
];

const platformControls = [
  {
    label: "Task intake",
    title: "Consistent request framing.",
    copy: "Task names and parameters should read the way internal teams already talk about solve flows.",
  },
  {
    label: "Routing logic",
    title: "Coverage without fragmentation.",
    copy: "Token, image, and audio workloads belong in one coherent platform story.",
  },
  {
    label: "Result delivery",
    title: "Polling or callback, depending on fit.",
    copy: "Some teams want immediate retrieval, others want callback-led orchestration and fewer brittle loops.",
  },
  {
    label: "Commercial layer",
    title: "The product story should hold at every tier.",
    copy: "Sandbox, production, scale, and enterprise need to feel connected, not stitched together.",
  },
];

const developerCards = [
  {
    label: "Readable tasks",
    title: "Request bodies that do not need translation.",
    copy: "Task families stay plain enough for developers to recognize and reuse quickly inside internal codebases.",
  },
  {
    label: "Callback ready",
    title: "Built for modern orchestration patterns.",
    copy: "Polling is still useful, but callback support keeps larger systems cleaner and easier to reason about.",
  },
  {
    label: "Browser friendly",
    title: "Aligned with real automation stacks.",
    copy: "The product positioning should feel native to Selenium, Playwright, Puppeteer, Cypress, and extension workflows.",
  },
];

const docsCards = [
  {
    label: "Quick start",
    title: "Short path from signup to first solve.",
    copy: "Free credits, a direct request example, and clear result retrieval should be visible immediately.",
  },
  {
    label: "SDKs",
    title: "Language support that covers common buying patterns.",
    copy: "Python and Node matter most, with Go and PHP helping broader engineering teams adopt without friction.",
  },
  {
    label: "Task guides",
    title: "Coverage explained by challenge family.",
    copy: "Teams want to see exactly how Turnstile, reCAPTCHA, Arkose, GeeTest, and harder surfaces are framed.",
  },
  {
    label: "Migration",
    title: "Switching should feel practical.",
    copy: "A clean site lowers perceived switching cost before engineering ever touches the first request.",
  },
];

const pricingRows = [
  {
    label: "Sandbox access",
    value: "Free credits for evaluation and integration work.",
  },
  {
    label: "Production usage",
    value: "Usage-based pricing across core challenge families.",
  },
  {
    label: "Scale discounts",
    value: "Better economics as throughput becomes predictable.",
  },
  {
    label: "Enterprise",
    value: "Custom packaging for procurement, dedicated lanes, and deeper commercial support.",
  },
];

const plans = [
  {
    name: "Sandbox",
    price: "Free credits",
    detail: "Evaluate coverage, SDKs, and browser workflows without procurement overhead.",
    features: ["Coverage preview", "Developer guides", "Low-friction evaluation"],
    cta: "Start in sandbox",
    featured: false,
  },
  {
    name: "Production",
    price: "Usage-based",
    detail: "Move into live traffic with a cleaner task model and flexible delivery modes.",
    features: ["Polling and callbacks", "ProxyLess and own proxy", "Built for real traffic"],
    cta: "Use the API",
    featured: true,
  },
  {
    name: "Scale",
    price: "Volume discounts",
    detail: "Improve economics and planning once traffic patterns stabilize and throughput increases.",
    features: ["Commercial review", "Discount lanes", "Migration support"],
    cta: "Talk to sales",
    featured: false,
  },
  {
    name: "Enterprise",
    price: "Custom",
    detail: "Add packaging for larger buying processes without changing the core product story.",
    features: ["Dedicated lanes", "Procurement support", "Custom onboarding"],
    cta: "Request enterprise",
    featured: false,
  },
];

const procurementCards = [
  {
    label: "Spend shape",
    title: "Costs should move with workload complexity.",
    copy: "Different challenge families have different economics, so pricing should feel structured rather than blunt.",
  },
  {
    label: "Buying motion",
    title: "Self-serve first, sales when it adds value.",
    copy: "The page should support quick starts and still make sense to teams that need legal, finance, or security review.",
  },
  {
    label: "Scale posture",
    title: "A credible enterprise lane matters.",
    copy: "Even when most users self-serve, a serious site still shows how larger teams can buy with confidence.",
  },
];

const pricingQuestions = [
  {
    question: "Why not publish one flat number?",
    answer:
      "Solve families vary too much for that to feel honest. A cleaner approach is family-aware usage pricing with clear lanes for bigger buyers.",
  },
  {
    question: "Is there a low-friction entry point?",
    answer:
      "Yes. The sandbox exists to get teams from curiosity to integration without early commercial friction.",
  },
  {
    question: "When do discounts usually matter?",
    answer:
      "Once throughput becomes predictable and operational needs are stable enough to model more deliberately.",
  },
  {
    question: "What does enterprise packaging add?",
    answer:
      "It creates room for procurement, support expectations, dedicated lanes, and a more formal commercial relationship.",
  },
];

const solutionCards = [
  {
    label: "Growth flows",
    title: "Signup and onboarding automation.",
    copy: "When solve quality touches acquisition or expansion workflows, reliability and clean orchestration matter quickly.",
  },
  {
    label: "Account operations",
    title: "Recurring browser tasks at scale.",
    copy: "Teams maintaining ongoing account activity need a calmer surface for routing, results, and proxy strategy.",
  },
  {
    label: "Monitoring",
    title: "Research, intelligence, and high-frequency checks.",
    copy: "Programs that run continuously care about throughput shape, spend clarity, and delivery flexibility.",
  },
  {
    label: "QA and internal tooling",
    title: "Testing the same paths real users hit.",
    copy: "A strong solver platform helps internal teams automate hard browser flows without improvising around every challenge family.",
  },
];

const switchReasons = [
  {
    label: "Coverage",
    title: "Broad enough to replace fragmented stacks.",
    copy: "Teams leave older vendors when challenge family support stops matching the reality of modern sites.",
  },
  {
    label: "Ergonomics",
    title: "Cleaner than the category default.",
    copy: "Readable requests, clear docs, and a calmer presentation reduce the perceived cost of switching.",
  },
  {
    label: "Commercial fit",
    title: "Built for both fast trials and formal buying.",
    copy: "A product in this space needs to make sense for solo evaluators and larger teams with purchasing process.",
  },
];

const deploymentCards = [
  {
    label: "Early programs",
    title: "Start with exploration, not commitment.",
    copy: "Teams just proving fit want free credits, clear docs, and a fast path to first success.",
  },
  {
    label: "Burst traffic",
    title: "Handle uneven demand gracefully.",
    copy: "Some programs spike around launches, campaigns, or batch operations and need flexible delivery and pricing.",
  },
  {
    label: "Always-on agents",
    title: "Stay legible when throughput becomes constant.",
    copy: "Long-running browser systems value consistency, routing clarity, and commercial terms that improve with scale.",
  },
];

const requestSnippet = `POST /v1/tasks
{
  "type": "TurnstileTaskProxyLess",
  "websiteURL": "https://app.example.com/signup",
  "websiteKey": "0x4AAAAAA...",
  "callbackUrl": "https://ops.example.com/solve"
}`;

const responseSnippet = `{
  "taskId": "tsk_98ab2",
  "status": "ready",
  "solution": {
    "token": "0.1f4f8a..."
  }
}`;

function normalizePathname(pathname: string): RoutePath {
  if (
    pathname === "/" ||
    pathname === "/platform" ||
    pathname === "/developers" ||
    pathname === "/pricing" ||
    pathname === "/solutions"
  ) {
    return pathname;
  }

  return "/";
}

function applyPageMeta(pathname: RoutePath) {
  const meta = pageMeta[pathname];
  document.title = meta.title;

  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
  const ogDescription = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
  const twitterTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
  const twitterDescription = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');

  description?.setAttribute("content", meta.description);
  ogTitle?.setAttribute("content", meta.title);
  ogDescription?.setAttribute("content", meta.description);
  twitterTitle?.setAttribute("content", meta.title);
  twitterDescription?.setAttribute("content", meta.description);
}

function SectionIntro({
  toneClass,
  kicker,
  title,
  copy,
}: {
  toneClass: string;
  kicker: string;
  title: ReactNode;
  copy?: string;
}) {
  return (
    <div className="section-intro">
      <div className="eyebrow">
        <span className={`mini-dot ${toneClass}`} />
        {kicker}
      </div>
      <h2 className="section-title">{title}</h2>
      {copy ? <p className="section-copy">{copy}</p> : null}
    </div>
  );
}

function ConsolePanel({ label, code }: { label: string; code: string }) {
  return (
    <div className="console-panel">
      <div className="console-head">
        <span className="panel-kicker">{label}</span>
        <div className="console-top">
          <span className="console-dot" />
          <span className="console-dot" />
          <span className="console-dot" />
        </div>
      </div>
      <pre>{code}</pre>
    </div>
  );
}

function TagPanel({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="panel-card">
      <div className="panel-kicker">{title}</div>
      <div className="tag-grid">
        {items.map(item => (
          <span key={item} className="tag-chip">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function PageHero({
  toneClass,
  kicker,
  title,
  lead,
  primaryHref,
  primaryLabel,
  secondaryHref,
  secondaryLabel,
  ribbon,
  visual,
}: {
  toneClass: string;
  kicker: string;
  title: ReactNode;
  lead: string;
  primaryHref: string;
  primaryLabel: string;
  secondaryHref: string;
  secondaryLabel: string;
  ribbon: string[];
  visual: ReactNode;
}) {
  return (
    <section className="section-rule">
      <div className="site-container hero-layout">
        <div className="hero-copy">
          <div className="eyebrow">
            <span className={`mini-dot ${toneClass}`} />
            {kicker}
          </div>

          <h1 className="hero-title">{title}</h1>
          <p className="hero-lead">{lead}</p>

          <div className="button-row">
            <a href={primaryHref} className="button-shell button-dark">
              {primaryLabel}
            </a>
            <a href={secondaryHref} className="button-shell button-light">
              {secondaryLabel}
            </a>
          </div>

          <div className="hero-ribbon">
            {ribbon.map(item => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>

        <div className="hero-visual">{visual}</div>
      </div>
    </section>
  );
}

function SiteCta({
  title,
  copy,
  primaryHref,
  primaryLabel,
  secondaryHref,
  secondaryLabel,
}: {
  title: string;
  copy: string;
  primaryHref: string;
  primaryLabel: string;
  secondaryHref: string;
  secondaryLabel: string;
}) {
  return (
    <section className="section-cta">
      <div className="site-container cta-card">
        <div className="cta-copy">
          <div className="eyebrow eyebrow-inverted">
            <span className="mini-dot mini-dot-yellow" />
            Ready to launch
          </div>
          <h2>{title}</h2>
          <p>{copy}</p>
        </div>

        <div className="button-row">
          <a href={primaryHref} className="button-shell button-ghost">
            {primaryLabel}
          </a>
          <a href={secondaryHref} className="button-shell button-white">
            {secondaryLabel}
          </a>
        </div>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <>
      <PageHero
        toneClass="mini-dot-red"
        kicker="Captcha infrastructure"
        title={
          <>
            A cleaner solver stack for <span className="hero-emphasis">production automation.</span>
          </>
        }
        lead="Solve reCAPTCHA, Turnstile, hCaptcha, Arkose, GeeTest, DataDome, AWS WAF, image, and audio through one deliberate API. Clean task design, browser-ready tooling, and pricing that stays sensible as volume grows."
        primaryHref="/pricing#plans"
        primaryLabel="Get sandbox access"
        secondaryHref="/platform"
        secondaryLabel="View platform"
        ribbon={["ProxyLess + own proxy", "Polling + callbacks", "SDKs + browser tooling"]}
        visual={
          <div className="showcase-card">
            <div className="showcase-head">
              <div>
                <span className="panel-kicker">Live product surface</span>
                <p className="showcase-title">Coverage, routing, and delivery in one calm frame.</p>
              </div>
              <div className="showcase-tags">
                <span className="showcase-tag">Turnstile</span>
                <span className="showcase-tag">Arkose</span>
                <span className="showcase-tag">DataDome</span>
              </div>
            </div>

            <div className="showcase-image">
              <img src={heroGraphic} alt="CaptchaSolver platform illustration" />
            </div>

            <div className="showcase-foot">
              <div className="showcase-stat">
                <strong>Readable task types</strong>
                <span>Made to feel clearer than legacy solve APIs.</span>
              </div>
              <div className="showcase-stat">
                <strong>Callback-ready</strong>
                <span>Works for polling loops and orchestration-first systems.</span>
              </div>
              <div className="showcase-stat">
                <strong>Commercially clean</strong>
                <span>Free sandbox, usage pricing, and enterprise lanes.</span>
              </div>
            </div>
          </div>
        }
      />

      <section className="section-rule">
        <div className="site-container section-stack section-stack-tight">
          <div className="capability-grid">
            {capabilityCards.map(card => (
              <article key={card.title} className="capability-card">
                <span className="panel-kicker">{card.label}</span>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="coverage" className="section-rule">
        <div className="site-container section-stack feature-layout">
          <div>
            <SectionIntro
              toneClass="mini-dot-blue"
              kicker="Coverage"
              title="Everything buyers compare, without the clutter."
              copy="The challenge families, delivery modes, and integration surface teams actually ask about."
            />

            <div className="panel-stack panel-stack-spaced">
              <TagPanel title="Challenge families" items={challengeFamilies} />
              <div className="panel-card">
                <div className="panel-kicker">Delivery modes</div>
                <div className="row-list">
                  {deliveryModes.map(item => (
                    <div key={item} className="row-item">
                      <span className="mini-dot mini-dot-black" />
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="panel-stack">
            <div className="graphic-panel">
              <div className="panel-headline">
                <span className="panel-kicker">Signal and routing</span>
                <h3>Broad coverage needs a legible orchestration story.</h3>
              </div>
              <img src={chartRouting} alt="Coverage and routing chart" />
            </div>

            <div className="cards-grid cards-grid-2">
              {productPillars.map(item => (
                <article key={item.title} className="info-card">
                  <span className="panel-kicker">{item.label}</span>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
              <article className="info-card info-card-emphasis">
                <span className="panel-kicker">Positioning</span>
                <h3>Designed to feel sharper than the category default.</h3>
                <p>Less clutter, better hierarchy, and a sales story that still reads credibly to developers.</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section id="platform" className="section-rule section-soft">
        <div className="site-container section-stack split-layout">
          <div className="split-copy">
            <SectionIntro
              toneClass="mini-dot-pink"
              kicker="Platform"
              title="A calmer control plane for a noisy category."
              copy="Readable for buyers, structured for developers, and persuasive enough to support a real sales motion."
            />

            <div className="cards-grid cards-grid-3 cards-grid-top">
              {productPillars.map(item => (
                <article key={`${item.label}-platform`} className="info-card">
                  <span className="panel-kicker">{item.label}</span>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </article>
              ))}
            </div>
          </div>

          <div className="graphic-panel graphic-panel-tall">
            <div className="panel-headline">
              <span className="panel-kicker">Platform map</span>
              <h3>Challenge intake, routing, delivery, and control surfaces laid out clearly.</h3>
            </div>
            <img src={pipelineGraphic} alt="CaptchaSolver platform pipeline" />
          </div>
        </div>
      </section>

      <section id="developers" className="section-rule">
        <div className="site-container section-stack developer-layout">
          <div>
            <SectionIntro
              toneClass="mini-dot-yellow"
              kicker="Developers"
              title="Shipping should feel quicker than onboarding."
              copy="Typed SDKs, plain request bodies, callback support, and browser automation fit without the usual category mess."
            />

            <div className="cards-grid cards-grid-3 cards-grid-top">
              {developerCards.map(card => (
                <article key={card.title} className="info-card">
                  <span className="panel-kicker">{card.label}</span>
                  <h3>{card.title}</h3>
                  <p>{card.copy}</p>
                </article>
              ))}
            </div>

            <TagPanel title="SDKs and frameworks" items={developerStack} />
          </div>

          <div className="console-grid">
            <ConsolePanel label="Create task" code={requestSnippet} />
            <ConsolePanel label="Ready result" code={responseSnippet} />
          </div>
        </div>
      </section>

      <section id="pricing" className="section-rule">
        <div className="site-container section-stack">
          <SectionIntro
            toneClass="mini-dot-red"
            kicker="Pricing"
            title={
              <>
                Self-serve at the start. <span className="muted-accent">Commercially ready when scale shows up.</span>
              </>
            }
            copy="A free sandbox, family-aware usage pricing, discount lanes at volume, and enterprise packaging when buying gets formal."
          />

          <div className="pricing-layout pricing-layout-top">
            <div className="pricing-model-card">
              <span className="panel-kicker">How pricing works</span>
              <h3>Simple to read, flexible enough to buy.</h3>
              <div className="pricing-model-list">
                {pricingRows.map(row => (
                  <div key={row.label} className="pricing-model-row">
                    <strong>{row.label}</strong>
                    <p>{row.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="plan-grid">
              {plans.map(plan => (
                <article key={plan.name} className={`plan-card ${plan.featured ? "is-featured" : ""}`}>
                  <div className="plan-top">
                    <span className="plan-name">{plan.name}</span>
                    {plan.featured ? <span className="plan-badge">Most balanced</span> : null}
                  </div>
                  <h3>{plan.price}</h3>
                  <p className="plan-detail">{plan.detail}</p>
                  <div className="plan-list">
                    {plan.features.map(item => (
                      <div key={item} className="row-item">
                        <span className="mini-dot mini-dot-black" />
                        <p>{item}</p>
                      </div>
                    ))}
                  </div>
                  <a href="/developers" className={`button-shell ${plan.featured ? "button-dark" : "button-light"} button-full`}>
                    {plan.cta}
                  </a>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="questions" className="section-rule">
        <div className="site-container section-stack">
          <SectionIntro
            toneClass="mini-dot-blue"
            kicker="Buyer questions"
            title="The questions serious teams ask before they switch."
          />

          <div className="cards-grid cards-grid-2 cards-grid-top">
            {buyerQuestions.map(item => (
              <article key={item.question} className="faq-card">
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <SiteCta
        title="Launch with a site that looks as credible as the product story."
        copy="Cleaner hierarchy, sharper positioning, and enough depth to support both self-serve buyers and formal sales conversations."
        primaryHref="/pricing#plans"
        primaryLabel="Review pricing"
        secondaryHref="/developers"
        secondaryLabel="See developer surface"
      />
    </>
  );
}

function PlatformPage() {
  return (
    <>
      <PageHero
        toneClass="mini-dot-pink"
        kicker="Platform"
        title={
          <>
            Operational clarity for <span className="hero-emphasis">every solve request.</span>
          </>
        }
        lead="Route every solve through one surface: task creation, proxy strategy, callbacks, result retrieval, and the controls that keep the platform legible under pressure."
        primaryHref="/developers"
        primaryLabel="See developer flow"
        secondaryHref="/pricing"
        secondaryLabel="View pricing"
        ribbon={["Task intake", "Routing logic", "Delivery modes"]}
        visual={
          <div className="showcase-card">
            <div className="showcase-head">
              <div>
                <span className="panel-kicker">Platform map</span>
                <p className="showcase-title">A product story that stays coherent from sandbox to enterprise.</p>
              </div>
              <div className="showcase-tags">
                <span className="showcase-tag">Routing</span>
                <span className="showcase-tag">Callbacks</span>
                <span className="showcase-tag">Controls</span>
              </div>
            </div>

            <div className="showcase-image">
              <img src={pipelineGraphic} alt="Platform orchestration graphic" />
            </div>

            <div className="showcase-foot">
              <div className="showcase-stat">
                <strong>Intake</strong>
                <span>Keep task creation plain and fast to reason about.</span>
              </div>
              <div className="showcase-stat">
                <strong>Routing</strong>
                <span>Support modern challenge families without fragmenting the story.</span>
              </div>
              <div className="showcase-stat">
                <strong>Control</strong>
                <span>Surface spend and throughput in terms operators can actually use.</span>
              </div>
            </div>
          </div>
        }
      />

      <section className="section-rule">
        <div className="site-container section-stack">
          <SectionIntro
            toneClass="mini-dot-blue"
            kicker="Lifecycle"
            title="The solve lifecycle, laid out clearly."
            copy="A strong platform page explains how requests enter the system, how results move back out, and how teams stay in control as usage expands."
          />

          <div className="cards-grid cards-grid-4 cards-grid-top">
            {platformLifecycle.map(item => (
              <article key={item.title} className="info-card">
                <span className="step-number">{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-rule section-soft">
        <div className="site-container section-stack feature-layout">
          <div className="graphic-panel graphic-panel-tall">
            <div className="panel-headline">
              <span className="panel-kicker">Signal view</span>
              <h3>Coverage is only persuasive when the operating model feels clean.</h3>
            </div>
            <img src={chartRouting} alt="Signal routing and coverage chart" />
          </div>

          <div>
            <SectionIntro
              toneClass="mini-dot-red"
              kicker="Control surfaces"
              title="The details that make a solver platform feel mature."
              copy="Modern buyers are not just comparing challenge support. They are comparing clarity, migration cost, and how the platform behaves inside production automation."
            />

            <div className="cards-grid cards-grid-2 cards-grid-top">
              {platformControls.map(card => (
                <article key={card.title} className="info-card">
                  <span className="panel-kicker">{card.label}</span>
                  <h3>{card.title}</h3>
                  <p>{card.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SiteCta
        title="Make the platform page do real sales work."
        copy="It should reassure buyers, support developer scrutiny, and make switching feel possible without bloating the story."
        primaryHref="/developers"
        primaryLabel="Explore developers"
        secondaryHref="/pricing"
        secondaryLabel="Review pricing"
      />
    </>
  );
}

function DevelopersPage() {
  return (
    <>
      <PageHero
        toneClass="mini-dot-yellow"
        kicker="Developers"
        title={
          <>
            Developer ergonomics that <span className="hero-emphasis">hold up in production.</span>
          </>
        }
        lead="Readable task names, clear result objects, callback support, and browser automation alignment make integration feel faster and less brittle."
        primaryHref="/pricing#plans"
        primaryLabel="Start with sandbox"
        secondaryHref="/platform"
        secondaryLabel="See platform"
        ribbon={["Readable requests", "Typed SDKs", "Polling + callbacks"]}
        visual={
          <div className="developer-hero-stack">
            <ConsolePanel label="Create task" code={requestSnippet} />
            <ConsolePanel label="Ready result" code={responseSnippet} />
          </div>
        }
      />

      <section className="section-rule">
        <div className="site-container section-stack">
          <SectionIntro
            toneClass="mini-dot-blue"
            kicker="Tooling"
            title="Built around the stacks people already run."
            copy="The developer story has to feel native to browser automation teams, not bolted on after the fact."
          />

          <TagPanel title="SDKs and frameworks" items={developerStack} />
        </div>
      </section>

      <section className="section-rule section-soft">
        <div className="site-container section-stack">
          <SectionIntro
            toneClass="mini-dot-pink"
            kicker="Developer surface"
            title="Small decisions that remove a lot of friction."
            copy="A credible developer page keeps the pitch concrete: what requests look like, how results return, and why the migration path feels manageable."
          />

          <div className="cards-grid cards-grid-3 cards-grid-top">
            {developerCards.map(card => (
              <article key={card.title} className="info-card">
                <span className="panel-kicker">{card.label}</span>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>

          <div className="cards-grid cards-grid-2 cards-grid-top">
            {docsCards.map(card => (
              <article key={card.title} className="info-card">
                <span className="panel-kicker">{card.label}</span>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <SiteCta
        title="Keep the developer story crisp."
        copy="The fastest way to build trust in this category is to show exactly how the API feels, then get out of the way."
        primaryHref="/pricing"
        primaryLabel="View pricing"
        secondaryHref="/solutions"
        secondaryLabel="See use cases"
      />
    </>
  );
}

function PricingPage() {
  return (
    <>
      <PageHero
        toneClass="mini-dot-red"
        kicker="Pricing"
        title={
          <>
            Commercially simple. <span className="hero-emphasis">Operationally flexible.</span>
          </>
        }
        lead="Start in a free sandbox, move into usage pricing for live traffic, improve economics at scale, and keep a clear path for larger buying processes."
        primaryHref="#plans"
        primaryLabel="Review plans"
        secondaryHref="/developers"
        secondaryLabel="Explore developers"
        ribbon={["Free sandbox", "Usage pricing", "Scale + enterprise lanes"]}
        visual={
          <div className="showcase-card">
            <div className="showcase-head">
              <div>
                <span className="panel-kicker">Spend view</span>
                <p className="showcase-title">A pricing story that looks deliberate before sales ever gets involved.</p>
              </div>
              <div className="showcase-tags">
                <span className="showcase-tag">Usage</span>
                <span className="showcase-tag">Discounts</span>
                <span className="showcase-tag">Enterprise</span>
              </div>
            </div>

            <div className="showcase-image">
              <img src={chartSpend} alt="Pricing and spend chart" />
            </div>

            <div className="showcase-foot">
              <div className="showcase-stat">
                <strong>Entry</strong>
                <span>Free evaluation lowers friction for technical teams.</span>
              </div>
              <div className="showcase-stat">
                <strong>Growth</strong>
                <span>Usage-based pricing stays aligned with real traffic.</span>
              </div>
              <div className="showcase-stat">
                <strong>Scale</strong>
                <span>Better packaging exists when throughput becomes meaningful.</span>
              </div>
            </div>
          </div>
        }
      />

      <section id="plans" className="section-rule">
        <div className="site-container section-stack">
          <SectionIntro
            toneClass="mini-dot-blue"
            kicker="Plans"
            title="A pricing ladder that reads cleanly."
            copy="It should be obvious where a new team starts, how a live deployment grows, and what changes when buying becomes more formal."
          />

          <div className="plan-grid plan-grid-full cards-grid-top">
            {plans.map(plan => (
              <article key={plan.name} className={`plan-card ${plan.featured ? "is-featured" : ""}`}>
                <div className="plan-top">
                  <span className="plan-name">{plan.name}</span>
                  {plan.featured ? <span className="plan-badge">Most balanced</span> : null}
                </div>
                <h3>{plan.price}</h3>
                <p className="plan-detail">{plan.detail}</p>
                <div className="plan-list">
                  {plan.features.map(item => (
                    <div key={item} className="row-item">
                      <span className="mini-dot mini-dot-black" />
                      <p>{item}</p>
                    </div>
                  ))}
                </div>
                <a href="/developers" className={`button-shell ${plan.featured ? "button-dark" : "button-light"} button-full`}>
                  {plan.cta}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-rule section-soft">
        <div className="site-container section-stack feature-layout">
          <div className="graphic-panel graphic-panel-tall">
            <div className="panel-headline">
              <span className="panel-kicker">Spend model</span>
              <h3>Costs should move with workload shape, not fight it.</h3>
            </div>
            <img src={chartSpend} alt="Spend and pricing visualization" />
          </div>

          <div>
            <SectionIntro
              toneClass="mini-dot-yellow"
              kicker="Commercial logic"
              title="Pricing needs to feel honest before it feels persuasive."
              copy="This category gets scrutinized fast. The page should explain how spend behaves, why bigger buyers get different lanes, and where enterprise packaging fits."
            />

            <div className="cards-grid cards-grid-3 cards-grid-top">
              {procurementCards.map(card => (
                <article key={card.title} className="info-card">
                  <span className="panel-kicker">{card.label}</span>
                  <h3>{card.title}</h3>
                  <p>{card.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="section-rule">
        <div className="site-container section-stack">
          <SectionIntro toneClass="mini-dot-pink" kicker="FAQ" title="The commercial questions teams raise before they buy." />

          <div className="cards-grid cards-grid-2 cards-grid-top">
            {pricingQuestions.map(item => (
              <article key={item.question} className="faq-card">
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <SiteCta
        title="Make pricing feel clear before the first call."
        copy="A great pricing page lowers friction for evaluators and still gives larger teams an obvious path into sales."
        primaryHref="/developers"
        primaryLabel="See developers"
        secondaryHref="/platform"
        secondaryLabel="View platform"
      />
    </>
  );
}

function SolutionsPage() {
  return (
    <>
      <PageHero
        toneClass="mini-dot-blue"
        kicker="Use cases"
        title={
          <>
            Built for flows where failed solves create <span className="hero-emphasis">real cost.</span>
          </>
        }
        lead="When captcha handling sits inside growth, operations, monitoring, or QA, teams need more than raw coverage. They need a surface that stays clear under pressure."
        primaryHref="/pricing"
        primaryLabel="See pricing"
        secondaryHref="/platform"
        secondaryLabel="View platform"
        ribbon={["Growth", "Operations", "Monitoring", "QA"]}
        visual={
          <div className="showcase-card">
            <div className="showcase-head">
              <div>
                <span className="panel-kicker">Use case map</span>
                <p className="showcase-title">A solver platform should stretch across multiple program shapes.</p>
              </div>
              <div className="showcase-tags">
                <span className="showcase-tag">Automation</span>
                <span className="showcase-tag">Monitoring</span>
                <span className="showcase-tag">Testing</span>
              </div>
            </div>

            <div className="showcase-image">
              <img src={heroGraphic} alt="Use case illustration" />
            </div>

            <div className="showcase-foot">
              <div className="showcase-stat">
                <strong>Growth</strong>
                <span>Support flows where reliability touches acquisition.</span>
              </div>
              <div className="showcase-stat">
                <strong>Ops</strong>
                <span>Keep recurring browser work legible and controllable.</span>
              </div>
              <div className="showcase-stat">
                <strong>QA</strong>
                <span>Automate real-world challenge paths inside testing programs.</span>
              </div>
            </div>
          </div>
        }
      />

      <section className="section-rule">
        <div className="site-container section-stack">
          <SectionIntro
            toneClass="mini-dot-red"
            kicker="Programs"
            title="The business cases this category keeps running into."
            copy="Different teams buy solver infrastructure for different reasons, but they all care about clarity, fit, and time to confidence."
          />

          <div className="cards-grid cards-grid-4 cards-grid-top">
            {solutionCards.map(card => (
              <article key={card.title} className="info-card">
                <span className="panel-kicker">{card.label}</span>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-rule section-soft">
        <div className="site-container section-stack">
          <SectionIntro
            toneClass="mini-dot-yellow"
            kicker="Why teams move"
            title="A better solver pitch has three jobs."
            copy="It needs to broaden coverage, lower switching anxiety, and make the buying motion feel reasonable from the first minute."
          />

          <div className="cards-grid cards-grid-3 cards-grid-top">
            {switchReasons.map(card => (
              <article key={card.title} className="info-card">
                <span className="panel-kicker">{card.label}</span>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>

          <div className="cards-grid cards-grid-3 cards-grid-top">
            {deploymentCards.map(card => (
              <article key={card.title} className="info-card">
                <span className="panel-kicker">{card.label}</span>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <SiteCta
        title="Turn the use-case page into a conversion page."
        copy="Show where the platform fits, why teams switch, and how the commercial path stays sensible as programs mature."
        primaryHref="/pricing"
        primaryLabel="Review pricing"
        secondaryHref="/developers"
        secondaryLabel="Explore developers"
      />
    </>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="site-container footer-grid">
        <div className="footer-brand">
          <a href="/" className="brand-lockup">
            <img src={logo} alt="CaptchaSolver logo" className="brand-mark brand-mark-footer" />
            <div className="brand-copy">
              <span className="brand-name">CaptchaSolver</span>
              <span className="brand-subtitle">captcha infrastructure</span>
            </div>
          </a>
          <p>Modern captcha infrastructure for browser automation, developer teams, and commercial operators who want a cleaner category leader.</p>
        </div>

        <div className="footer-column">
          <span className="panel-kicker">Product</span>
          <a href="/">Home</a>
          <a href="/platform">Platform</a>
          <a href="/developers">Developers</a>
          <a href="/pricing">Pricing</a>
        </div>

        <div className="footer-column">
          <span className="panel-kicker">Solutions</span>
          <a href="/solutions">Use cases</a>
          <a href="/#coverage">Coverage</a>
          <a href="/#questions">Buyer questions</a>
          <a href="/pricing#faq">Pricing FAQ</a>
        </div>

        <div className="footer-column">
          <span className="panel-kicker">Path</span>
          <a href="/pricing#plans">Get sandbox access</a>
          <a href="/developers">Developer surface</a>
          <a href="/platform">Platform overview</a>
          <a href="/pricing">Talk to sales</a>
        </div>
      </div>
    </footer>
  );
}

export function App() {
  const pathname = typeof window === "undefined" ? "/" : normalizePathname(window.location.pathname);

  useEffect(() => {
    applyPageMeta(pathname);
  }, [pathname]);

  return (
    <div className="site-shell">
      <div className="announcement-bar">
        <div className="site-container announcement-inner">
          <div className="announcement-copy">
            <span className="mini-dot mini-dot-yellow" />
            <p>Production-grade captcha infrastructure for modern automation teams.</p>
          </div>
          <a href="/solutions" className="announcement-link">
            Use cases
          </a>
        </div>
      </div>

      <header className="site-header">
        <div className="site-container navbar">
          <a href="/" className="brand-lockup">
            <img src={logo} alt="CaptchaSolver logo" className="brand-mark" />
            <div className="brand-copy">
              <span className="brand-name">CaptchaSolver</span>
              <span className="brand-subtitle">captcha infrastructure</span>
            </div>
          </a>

          <nav className="nav-links" aria-label="Primary">
            {siteNav.map(item => {
              const isActive = pathname === item.href;

              return (
                <a
                  key={item.href}
                  href={item.href}
                  className={`nav-link ${isActive ? "is-active" : ""}`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          <div className="nav-actions">
            <a href="/pricing" className="button-shell button-light">
              Talk to sales
            </a>
            <a href="/pricing#plans" className="button-shell button-dark">
              Get sandbox access
            </a>
          </div>
        </div>
      </header>

      <main className="page-shell">
        {pathname === "/" ? <HomePage /> : null}
        {pathname === "/platform" ? <PlatformPage /> : null}
        {pathname === "/developers" ? <DevelopersPage /> : null}
        {pathname === "/pricing" ? <PricingPage /> : null}
        {pathname === "/solutions" ? <SolutionsPage /> : null}
      </main>

      <Footer />
    </div>
  );
}

export default App;
